// API service for agent management

import { APIResponse, PaginationParams } from '../../shared/types';
import { 
  AgentConfig, 
  AgentCreateRequest, 
  AgentUpdateRequest, 
  AgentListResponse,
  AgentTemplate 
} from '../../shared/types/agent';

const API_BASE = '/api';

export class AgentApiService {
  // Get all agents for the current user
  async list(params?: PaginationParams): Promise<APIResponse<AgentListResponse>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const response = await fetch(`${API_BASE}/agents?${searchParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  // Get a specific agent by ID
  async get(agentId: string): Promise<APIResponse<AgentConfig>> {
    const response = await fetch(`${API_BASE}/agents/${agentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  // Create a new agent
  async create(data: AgentCreateRequest): Promise<APIResponse<AgentConfig>> {
    const response = await fetch(`${API_BASE}/agents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  }

  // Update an existing agent
  async update(agentId: string, data: AgentUpdateRequest): Promise<APIResponse<AgentConfig>> {
    const response = await fetch(`${API_BASE}/agents/${agentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify(data),
    });

    return response.json();
  }

  // Delete an agent
  async delete(agentId: string): Promise<APIResponse<void>> {
    const response = await fetch(`${API_BASE}/agents/${agentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  // Get available tools (internal + MCP)
  async getAvailableTools(): Promise<APIResponse<{ internal: any[], mcp: any[] }>> {
    const response = await fetch(`${API_BASE}/agents/tools`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  // Get agent templates
  async getTemplates(): Promise<APIResponse<AgentTemplate[]>> {
    const response = await fetch(`${API_BASE}/agents/templates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  // Get available chat sessions (workflows)
  async getChatSessions(): Promise<APIResponse<any[]>> {
    const response = await fetch(`${API_BASE}/workflows`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  // Attach agent to chat session
  async attachToChatSession(agentId: string, workflowId: string): Promise<APIResponse<void>> {
    const response = await fetch(`${API_BASE}/agents/${agentId}/chat-sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
      body: JSON.stringify({ workflowId }),
    });

    return response.json();
  }

  // Detach agent from chat session
  async detachFromChatSession(agentId: string, workflowId: string): Promise<APIResponse<void>> {
    const response = await fetch(`${API_BASE}/agents/${agentId}/chat-sessions/${workflowId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAuthToken()}`,
      },
    });

    return response.json();
  }

  private getAuthToken(): string {
    // Get auth token from localStorage or context
    return localStorage.getItem('auth_token') || '';
  }
}

export const agentApi = new AgentApiService();
